// Phase 2.2 Lite — Public review page.
// Route: /review/:eventSlug?token=...
// Anonymous, token-protected. Renders invitation with existing
// TemplateRenderer + floating ReviewHeader. NO admin chrome, NO RSVP
// submit (settingsOverrides.disableRsvpSubmit=true is set so the
// renderer's RSVP form is inert).
// noindex,nofollow because review links are private.

import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import TemplateRenderer from '../components/renderer/TemplateRenderer';
import { V2_TEMPLATES, DEFAULT_V2_TEMPLATE_ID } from '../lib/utils/v2Templates';
import ReviewHeader from '../components/review/ReviewHeader';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicReviewPage() {
  const { eventSlug } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const fetchReview = useCallback(async () => {
    if (!eventSlug || !token) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    setNotFound(false);
    try {
      const r = await axios.get(
        `${API}/api/public/review/${encodeURIComponent(eventSlug)}?token=${encodeURIComponent(token)}`,
      );
      setReview(r.data);
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [eventSlug, token]);

  useEffect(() => { fetchReview(); }, [fetchReview]);

  // Local optimistic update for the status pill after Approve / Request changes.
  const handleStatusChange = (nextStatus) => {
    setReview((prev) => prev ? { ...prev, review_status: nextStatus } : prev);
  };

  // Private review pages must NEVER be indexed.
  const privateHead = (
    <Helmet>
      <meta name="robots" content="noindex, nofollow" />
      <meta name="googlebot" content="noindex, nofollow" />
      <title>Private review · Sitoa</title>
    </Helmet>
  );

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-gray-50"
        data-testid="public-review-loading"
      >
        {privateHead}
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  if (notFound || !review) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-gray-50"
        data-testid="public-review-not-found"
      >
        {privateHead}
        <div className="text-center px-4">
          <h1 className="text-xl font-light text-gray-700 mb-1">Review link unavailable</h1>
          <p className="text-xs text-gray-500">This link has expired, been disabled, or doesn&apos;t exist.</p>
        </div>
      </div>
    );
  }

  const baseId = review.v2_template_id || DEFAULT_V2_TEMPLATE_ID;
  const config = V2_TEMPLATES[baseId];
  if (!config) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-gray-50"
        data-testid="public-review-unsupported"
      >
        {privateHead}
        <div className="text-center px-4">
          <h1 className="text-xl font-light text-gray-700 mb-1">Review unavailable</h1>
          <p className="text-xs text-gray-500">This template is no longer supported.</p>
        </div>
      </div>
    );
  }

  // Disable RSVP submission while in review. The renderer reads
  // `disableRsvpSubmit` from settingsOverrides and keeps the form visually
  // present but inert — no POST goes through.
  const settingsOverrides = {
    ...(review.v2_settings_overrides || {}),
    disableRsvpSubmit: true,
    reviewMode: true,
  };

  return (
    <div className="min-h-screen" data-testid="public-review-page">
      {privateHead}
      <ReviewHeader
        status={review.review_status}
        eventSlug={review.event_slug || eventSlug}
        token={token}
        onStatusChange={handleStatusChange}
      />
      <TemplateRenderer
        templateConfig={config}
        invitationData={review.v2_content || {}}
        themeOverrides={review.v2_theme_overrides || {}}
        settingsOverrides={settingsOverrides}
        slug={`review-${review.event_slug || eventSlug}`}
        layoutVersion={review.layout_version || 'legacy-flat'}
      />
    </div>
  );
}
